import React, { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import styled, { css } from "styled-components";
import { ICustomer } from "types/interfaces/customers/ICustomer";
import sanityClient, { sanityImgBuilder } from "./sanity";
import { useHistory, useLocation } from "react-router-dom";
import { device, size } from "constants/Devices";
import { format, parseISO } from "date-fns";
import Logger from "services/logger";
import ArticleContent from "./components/ArticleContent";
import Testimonial from "./components/Testimonial";
import Figures from "./components/Figures";
import { Typography } from "@beamcloud/design-system";
import Company from "./components/CompanyLogo";
import BannerSection from "../components/BannerSection";
import { typography } from "../components/typography";

const Article: React.FC = () => {
  const history = useHistory();
  const location = useLocation();

  const [article, setArticle] = useState<ICustomer>();

  useEffect(() => {
    const slug = location.pathname
      .replace(new RegExp("/+$"), "")
      .split("/")
      .pop();

    if (!slug) {
      return;
    }

    (async (customerSlug: string) => {
      try {
        const customer: ICustomer = await sanityClient.fetch(
          `
          *[_type == "caseStudy" && slug.current == "${customerSlug}"][0]{
            "id": _id,
            title,
            description,
            company->{
              "id": _id,
              name,
              about,
              industry,
              link,
              "image": picture
            },
            content,
            date,
            coverImage,
            testimonial->{
              "id": _id,
              name,
              position,
              testimonial,
              image
            },
            "figures": figures[]{
              "id": _key,
              metric,
              description
            }
          }
        `
        );
        setArticle(customer);
      } catch (error) {
        Logger.error("fetchCustomer", error);
      }
    })(slug);
  }, [location.pathname]);

  return (
    <Container>
      {article && (
        <>
          <Helmet>
            <title>{article.title} • Beam</title>
            <link rel="canonical" href={`${window.location.origin}${location.pathname}`} />
            <meta property="og:type" content="article" />
            <meta property="og:title" content={`${article.title} • Beam`} />
            <meta property="og:image" content={sanityImgBuilder.image(article.coverImage).url()} />
            <meta property="og:url" content={window.location.href} />
            <meta name="twitter:title" content={`${article.title} • Beam`} />
            <meta name="twitter:image" content={sanityImgBuilder.image(article.coverImage).url()} />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>
          <CustomerTag>Customer Story</CustomerTag>
          <TitleWrapper>
            <ArticleTitle>{article.title}</ArticleTitle>
          </TitleWrapper>
          {article.figures && article.figures.length > 0 && <Figures figures={article.figures} />}
          {article.testimonial && <Testimonial testimonial={article.testimonial} />}
          <ContentWrapper>
            <Content>
              <ArticleContent content={article.content!} />
            </Content>
            {article.company && (
              <CompanyInfo>
                <CompanyInfoLogo
                  src={sanityImgBuilder.image(article.company.image).url()}
                  alt={article.company.name}
                />
                <CompanyInfoSection>
                  <CompanyInfoLabel>About</CompanyInfoLabel>
                  <CompanyInfoText>{article.company.about || "No information available"}</CompanyInfoText>
                </CompanyInfoSection>
                <CompanyInfoSection>
                  <CompanyInfoLabel>Industry</CompanyInfoLabel>
                  <CompanyInfoText>{article.company.industry || "Not specified"}</CompanyInfoText>
                </CompanyInfoSection>
                <CompanyInfoSection>
                  <CompanyInfoLabel>Link</CompanyInfoLabel>
                  {article.company.link ? (
                    <CompanyInfoLink href={article.company.link} target="_blank" rel="noopener noreferrer">
                      {article.company.link.replace(/^https?:\/\/(www\.)?/, "")}
                    </CompanyInfoLink>
                  ) : (
                    <CompanyInfoText>Not available</CompanyInfoText>
                  )}
                </CompanyInfoSection>
              </CompanyInfo>
            )}
          </ContentWrapper>
        </>
      )}
      <BannerSection />
    </Container>
  );
};

export default Article;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: 64px;
  align-items: center;
  padding-top: 48px;
  width: 100vw;
`;

const TitleWrapper = styled.div`
  display: flex;
  flex-direction: column;
  text-align: center;
  align-items: center;
  padding: 0px;
  gap: 32px;

  max-width: 800px;
  margin: 0 auto;

  @media ${device.mobileXL} {
    width: ${size.mobileS}px;
  }
`;

const Content = styled.div`
  display: flex;
  flex-direction: column;
  max-width: 640px;
  gap: 16px;
  margin-top: -24px;

  @media ${device.mobileXL} {
    width: ${size.mobileS}px;
  }
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex: 1;
  width: 100%;
  gap: 96px;
  justify-content: center;

  @media ${device.laptopM} {
    flex-direction: column;
    align-items: center;
    width: ${size.mobileXL}px;
  }

  @media ${device.mobileXL} {
    width: ${size.mobileS}px;
  }
`;

const CompanyInfo = styled.div`
  display: flex;
  flex-direction: column;
  width: 256px;
  gap: 24px;
  position: sticky;
  top: 96px;
  align-self: flex-start;
  max-height: calc(100vh - 96px);
  overflow-y: auto;

  @media ${device.laptopM} {
    width: 100%;
    max-width: 640px;
    position: relative;
    top: 0;
    max-height: none;
    overflow-y: visible;
  }
`;

const CompanyInfoSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`;

const CompanyInfoLogo = styled.img`
  height: 24px;
  margin-bottom: 8px;
  width: auto;
  object-fit: contain;
  max-width: 100%;
  align-self: flex-start;
`;

const CompanyInfoLabel = styled.div`
  ${typography.title16};
  color: ${props => props.theme.colors.white};
`;

const CompanyInfoText = styled(Typography).attrs({
  tag: "div",
  variant: "textBase",
  color: "gray300"
})`
  && {
    ${typography.textBaseLight};
    font-weight: 400;
  }
`;

const CompanyInfoLink = styled.a`
  ${typography.textBaseLight};
  color: ${props => props.theme.colors.gray300};
  text-decoration: none;
  &:hover {
    text-decoration: underline;
    color: ${props => props.theme.colors.white};
  }
`;

const CustomerTag = styled.div`
  border-radius: 8px;
  margin-bottom: -40px;
  padding: 2px 8px;
  background: rgba(62, 94, 221, 0.24);
  ${typography.textBaseLight};
  display: flex;
  align-items: center;
  justify-content: center;
  color: rgba(132, 157, 255, 1);
`;

const ArticleTitle = styled.h1`
  ${typography.title44};
  color: ${props => props.theme.colors.white};
  margin: 0;
`;
